import React from "react";
import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/styles";
import Tooltiper from "../../../../../components/Tooltiper";
import EquipmentRowName from "../../../../../components/EquipmentRowName";
import UserAvatar from "../../../../../components/UserAvatar";
import { ExternalOrderRecordEntity, TechnicalCondition } from "../../../../../graphql/graphQlApiHooks";
import { ContactsViewer, RenderStatus } from "../OrderList";

interface IdCellProps {
    row: ExternalOrderRecordEntity;
}

export const IdCell = React.memo(function IdCell({ row }: IdCellProps) {
    const theme = useTheme();
    return (
        <Tooltiper
            text={String(row.externalOrder.id)}
            props={{
                sx: {
                    fontSize: "0.75rem !important",
                    fontWeight: 600,
                    color: theme.palette.text.secondary
                }
            }}
        />
    );
}, (prevProps, nextProps) => {
    return prevProps.row.externalOrder.id === nextProps.row.externalOrder.id;
});

interface EquipmentCellProps {
    row: ExternalOrderRecordEntity;
}

export const EquipmentCell = React.memo(function EquipmentCell({ row }: EquipmentCellProps) {
    const { equipment } = row;
    
    if (!equipment) {
        return null;
    }
    
    const {
        name,
        inventoryNumber,
        mainPhoto,
        photos,
        inUse,
        technicalCondition,
        failureReason,
        isEditable
    } = equipment;

    const avatarUrl = mainPhoto ? mainPhoto.url : (photos && photos?.length > 0) ? photos?.[0].avatarUrl : undefined;
    const avatarUrlOrigin = mainPhoto ? mainPhoto.url : (photos && photos?.length > 0) ? photos?.[0].url : undefined;

    return (
        <EquipmentRowName
            name={name}
            inventoryNumber={inventoryNumber || "б/н"}
            inUse={inUse}
            technicalCondition={(technicalCondition === TechnicalCondition.Working)}
            avatarUrl={avatarUrl}
            avatarUrlOrigin={avatarUrlOrigin}
            failureReason={failureReason}
            isEditable={isEditable}
        />
    );
}, (prevProps, nextProps) => {
    const prev = prevProps.row.equipment;
    const next = nextProps.row.equipment;
    
    if (!prev || !next) return prev === next;
    
    return (
        prev.id === next.id &&
        prev.name === next.name &&
        prev.inventoryNumber === next.inventoryNumber &&
        prev.technicalCondition === next.technicalCondition &&
        prev.inUse === next.inUse &&
        prev.failureReason === next.failureReason &&
        prev.isEditable === next.isEditable &&
        prev.mainPhoto?.id === next.mainPhoto?.id &&
        prev.photos?.length === next.photos?.length
    );
});

interface OwnerCellProps {
    row: ExternalOrderRecordEntity;
}

export const OwnerCell = React.memo(function OwnerCell({ row }: OwnerCellProps) {
    const owner = row?.externalOrder?.externalOrderOwner;
    return (
        <Stack direction={"row"} spacing={1}>
            <UserAvatar
                user={owner}
                showTooltip={true}
            />
            <Tooltiper
                text={owner?.fio || owner?.login}
            />
        </Stack>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.row.externalOrder?.externalOrderOwner?.id === nextProps.row.externalOrder?.externalOrderOwner?.id &&
        prevProps.row.externalOrder?.externalOrderOwner?.fio === nextProps.row.externalOrder?.externalOrderOwner?.fio &&
        prevProps.row.externalOrder?.externalOrderOwner?.login === nextProps.row.externalOrder?.externalOrderOwner?.login
    );
});

interface ContactsCellProps {
    row: ExternalOrderRecordEntity;
}

export const ContactsCell = React.memo(function ContactsCell({ row }: ContactsCellProps) {
    return (
        <ContactsViewer
            giverName={row.externalOrder.receivingName}
            giverPhone={row.externalOrder.receivingPhone}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.row.externalOrder.receivingName === nextProps.row.externalOrder.receivingName &&
        prevProps.row.externalOrder.receivingPhone === nextProps.row.externalOrder.receivingPhone
    );
});

interface AddressCellProps {
    row: ExternalOrderRecordEntity;
}

export const AddressCell = React.memo(function AddressCell({ row }: AddressCellProps) {
    return (
        <Tooltiper text={row.externalOrder.targetPointAddress} />
    );
}, (prevProps, nextProps) => {
    return prevProps.row.externalOrder.targetPointAddress === nextProps.row.externalOrder.targetPointAddress;
});

interface StatusCellProps {
    row: ExternalOrderRecordEntity;
}

export const StatusCell = React.memo(function StatusCell({ row }: StatusCellProps) {
    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            lineHeight={"initial"}
            width={"100%"}
        >
            <RenderStatus status={row.externalOrder.status} />
        </Box>
    );
}, (prevProps, nextProps) => {
    return prevProps.row.externalOrder.status === nextProps.row.externalOrder.status;
});


